import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { errorMessage } from '@/lib/errors';

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(errorMessage(error));
      } else {
        setSent(true);
      }
    } catch {
      setError(t('forgotPassword.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] to-[#111111] p-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#B8860B]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D4AF37]/10 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center">
            <span className="text-3xl font-bold tracking-tight">
              <span className="text-[#B8860B]">Beauty</span>
              <span className="text-[#D4AF37]">Book</span>
              <span className="text-[#B8860B] text-2xl">AI</span>
            </span>
          </Link>
        </div>

        <div className="w-full space-y-6 p-8 rounded-2xl bg-[#1a1a1a]/80 backdrop-blur-sm border border-[#B8860B]/20 shadow-2xl">
          {sent ? (
            /* Success state */
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-white">{t('forgotPassword.sentTitle')}</h1>
              <p className="text-gray-400">
                {t('forgotPassword.sentDescription')}
              </p>
              <p className="text-sm text-[#B8860B] font-medium">{email}</p>
              <p className="text-xs text-gray-500">{t('forgotPassword.checkSpam')}</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm text-[#B8860B] hover:text-[#D4AF37] hover:underline mt-4"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          ) : (
            /* Form state */
            <>
              <div className="space-y-2 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#B8860B]/10 flex items-center justify-center">
                    <Mail className="w-7 h-7 text-[#B8860B]" />
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-white">{t('forgotPassword.title')}</h1>
                <p className="text-gray-400 text-sm">
                  {t('forgotPassword.subtitle')}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">{t('login.emailLabel')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    required
                    className="bg-[#0a0a0a] border-gray-700 text-white placeholder:text-gray-500 focus:border-[#B8860B] focus:ring-[#B8860B]/20"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-400">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#B8860B] hover:bg-[#D4AF37] text-black font-semibold transition-all duration-300"
                  disabled={loading || !email.trim()}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('forgotPassword.sending')}
                    </>
                  ) : (
                    t('forgotPassword.sendLink')
                  )}
                </Button>
              </form>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-[#B8860B] hover:text-[#D4AF37] hover:underline"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
